/**
 * How a tree-filter pick was made, for the `filters applied` event.
 *
 * `search`: the tree's search box had text when the id was ticked.
 * `browse`: ticked with an empty search box — expanding or drilling the tree.
 * `shortlist`: taken from the chips above the tree (recents, competitors, a
 * trial plan's companies) or the entity-info tooltip's "+".
 */
export type SelectionSource = 'search' | 'browse' | 'shortlist';

export type SelectionSourceMap = Map<string, SelectionSource>;

export type TreeSelectionAnalytics = {
  selection_source: SelectionSource | 'mixed' | 'unchanged';
  search_selection_count: number;
  browse_selection_count: number;
  shortlist_selection_count: number;
};

/**
 * Records `source` for every id newly present in `nextIds` and forgets the ids
 * no longer in it, so an entity that is unticked and ticked again later is
 * attributed to the later gesture rather than the first.
 */
export const recordPicks = (
  map: SelectionSourceMap,
  nextIds: readonly string[],
  source: SelectionSource
): void => {
  forgetPicks(map, nextIds);
  for (const id of nextIds) {
    if (!map.has(id)) map.set(id, source);
  }
};

/**
 * Forgets every id no longer in `keptIds`. The removal paths (the chip's
 * cross, an unticked shortlist entity, the info tooltip's toggle) bypass
 * `recordPicks`; without this a removed pick keeps its stale source and a
 * later re-pick inherits it instead of recording its own gesture.
 */
export const forgetPicks = (
  map: SelectionSourceMap,
  keptIds: readonly string[]
): void => {
  const kept = new Set(keptIds);
  for (const id of Array.from(map.keys())) {
    if (!kept.has(id)) map.delete(id);
  }
};

/**
 * The picks that are new relative to what the form opened with. Ids that were
 * already committed are ignored even if they were unticked and re-ticked, so a
 * plain resubmit or a removal-only edit reads as `unchanged`.
 *
 * An id with no recorded source reached the selection without passing a
 * recording wrapper; as far as the form can tell that is a tree pick with no
 * search text, so it counts as `browse` rather than skewing the totals.
 */
export const deriveSelectionSource = (
  committedIds: readonly string[],
  submittedIds: readonly string[],
  sourceById: ReadonlyMap<string, SelectionSource>
): TreeSelectionAnalytics => {
  const committed = new Set(committedIds);
  const counts: Record<SelectionSource, number> = {
    search: 0,
    browse: 0,
    shortlist: 0,
  };
  for (const id of submittedIds) {
    if (committed.has(id)) continue;
    counts[sourceById.get(id) ?? 'browse'] += 1;
  }

  const used = (Object.keys(counts) as SelectionSource[]).filter(
    (source) => counts[source] > 0
  );
  const selection_source = (() => {
    if (used.length === 0) return 'unchanged';
    const [only] = used;
    return used.length === 1 && only ? only : 'mixed';
  })();

  return {
    selection_source,
    search_selection_count: counts.search,
    browse_selection_count: counts.browse,
    shortlist_selection_count: counts.shortlist,
  };
};
